/**
 * Phone Number Validation Utilities
 * Supports US phone number formats with international prefix support
 */

// Regular expressions for different phone number formats
const phoneRegexes = {
  // US phone number patterns
  us10Digit: /^(\d{3})(\d{3})(\d{4})$/,                    // 1234567890
  usWithDashes: /^(\d{3})-(\d{3})-(\d{4})$/,               // 123-456-7890
  usWithDots: /^(\d{3})\.(\d{3})\.(\d{4})$/,               // 123.456.7890
  usWithSpaces: /^(\d{3})\s(\d{3})\s(\d{4})$/,             // 123 456 7890
  usWithParens: /^\((\d{3})\)\s?(\d{3})-?(\d{4})$/,        // (123) 456-7890 or (123)456-7890
  usWithCountryCode: /^(\+1|1)?\s?\(?(\d{3})\)?\s?[\s\-]?(\d{3})[\s\-]?(\d{4})$/, // +1 123 456 7890, 1-123-456-7890
};

/**
 * Validates if a phone number is in a valid US format
 */
export function isValidPhoneNumber(phone: string): boolean {
  if (!phone || typeof phone !== 'string') {
    return false;
  }

  // Clean the phone number - remove all non-digit characters except + at the start
  const cleanPhone = phone.trim();
  
  // Test against all supported patterns
  return Object.values(phoneRegexes).some(regex => regex.test(cleanPhone));
}

/**
 * Extracts only the 10-digit phone number from various formats
 */
export function extractDigits(phone: string): string {
  if (!phone) return '';
  
  // Remove all non-digit characters
  const digits = phone.replace(/\D/g, '');
  
  // If it starts with 1 and has 11 digits, remove the country code
  if (digits.length === 11 && digits.startsWith('1')) {
    return digits.substring(1);
  }
  
  // Return the digits (should be 10 for US numbers)
  return digits;
}

/**
 * Formats a phone number to the standard US format: +1 (123) 456-7890
 */
export function formatPhoneNumber(phone: string): string {
  if (!phone) return '';
  
  const digits = extractDigits(phone);
  
  // Must be exactly 10 digits for US phone number
  if (digits.length !== 10) {
    return phone; // Return original if invalid
  }
  
  const areaCode = digits.substring(0, 3);
  const exchange = digits.substring(3, 6);
  const number = digits.substring(6);
  
  return `+1 (${areaCode}) ${exchange}-${number}`;
}

/**
 * Formats phone number for display in forms (without country code)
 */
export function formatPhoneNumberForInput(phone: string): string {
  if (!phone) return '';
  
  const digits = extractDigits(phone);
  
  if (digits.length !== 10) {
    return phone; // Return original if invalid
  }
  
  const areaCode = digits.substring(0, 3);
  const exchange = digits.substring(3, 6);
  const number = digits.substring(6);
  
  return `(${areaCode}) ${exchange}-${number}`;
}

/**
 * Validates and provides specific error messages for phone numbers
 */
export function validatePhoneNumberWithError(phone: string): { isValid: boolean; error?: string } {
  if (!phone || !phone.trim()) {
    return { isValid: false, error: 'Phone number is required' };
  }

  const cleanPhone = phone.trim();
  const digits = extractDigits(cleanPhone);
  
  if (digits.length === 0) {
    return { isValid: false, error: 'Phone number must contain digits' };
  }
  
  if (digits.length < 10) {
    return { isValid: false, error: 'Phone number must be at least 10 digits' };
  }
  
  if (digits.length > 11) {
    return { isValid: false, error: 'Phone number cannot exceed 11 digits' };
  }
  
  if (digits.length === 11 && !digits.startsWith('1')) {
    return { isValid: false, error: 'Phone number with country code must start with 1' };
  }
  
  const workingDigits = digits.length === 11 ? digits.substring(1) : digits;
  
  // Check for invalid area codes (can't start with 0 or 1)
  if (workingDigits.startsWith('0') || workingDigits.startsWith('1')) {
    return { isValid: false, error: 'Invalid area code - cannot start with 0 or 1' };
  }
  
  // Check for common invalid patterns
  if (/^(\d)\1{9}$/.test(workingDigits)) { // All same digit
    return { isValid: false, error: 'Phone number cannot be all the same digit' };
  }
  
  if (workingDigits === '1234567890' || workingDigits === '0123456789') {
    return { isValid: false, error: 'Please enter a valid phone number' };
  }
  
  return { isValid: true };
}

/**
 * Auto-formats phone number as user types
 * Used for real-time formatting in input fields
 */
export function formatPhoneNumberAsUserTypes(input: string, _previousValue: string = ''): string {
  // Remove all non-digit characters
  const digits = input.replace(/\D/g, '');
  
  // Limit to 10 digits
  const limitedDigits = digits.substring(0, 10);
  
  if (limitedDigits.length === 0) {
    return '';
  }
  
  if (limitedDigits.length <= 3) {
    return `(${limitedDigits}`;
  }
  
  if (limitedDigits.length <= 6) {
    return `(${limitedDigits.substring(0, 3)}) ${limitedDigits.substring(3)}`;
  }
  
  return `(${limitedDigits.substring(0, 3)}) ${limitedDigits.substring(3, 6)}-${limitedDigits.substring(6)}`;
}

/**
 * Prepares phone number for API calls (E.164 format)
 * Returns format: +11234567890
 */
export function formatPhoneNumberForAPI(phone: string): string {
  if (!phone) return '';
  
  const digits = extractDigits(phone);
  
  if (digits.length !== 10) {
    return phone; // Return original if invalid
  }
  
  return `+1${digits}`;
}

/**
 * Validates phone number for delivery services (Uber Direct format)
 */
export function validatePhoneNumberForDelivery(phone: string): { isValid: boolean; formattedPhone?: string; error?: string } {
  const validation = validatePhoneNumberWithError(phone);
  
  if (!validation.isValid) {
    return { isValid: false, error: validation.error };
  }
  
  const formattedPhone = formatPhoneNumberForAPI(phone);
  
  return {
    isValid: true,
    formattedPhone,
  };
}

/**
 * Common phone number validation patterns for different use cases
 */
export const phoneValidationPatterns = {
  // For form validation
  required: (phone: string) => validatePhoneNumberWithError(phone),
  
  // For optional phone fields
  optional: (phone: string) => {
    if (!phone || !phone.trim()) {
      return { isValid: true };
    }
    return validatePhoneNumberWithError(phone);
  },
  
  // For delivery services
  delivery: (phone: string) => validatePhoneNumberForDelivery(phone),
  
  // For SMS/notifications
  sms: (phone: string) => {
    const validation = validatePhoneNumberWithError(phone);
    if (!validation.isValid) {
      return validation;
    }
    
    // Additional checks for SMS capability
    const digits = extractDigits(phone);
    const areaCode = digits.substring(0, 3);
    
    // Some area codes don't support SMS well
    const problematicAreaCodes = ['800', '888', '877', '866', '855', '844', '833', '822'];
    if (problematicAreaCodes.includes(areaCode)) {
      return { isValid: false, error: 'This phone number may not support SMS notifications' };
    }
    
    return { isValid: true, formattedPhone: formatPhoneNumberForAPI(phone) };
  }
};

export default {
  isValid: isValidPhoneNumber,
  format: formatPhoneNumber,
  formatForInput: formatPhoneNumberForInput,
  formatForAPI: formatPhoneNumberForAPI,
  formatAsUserTypes: formatPhoneNumberAsUserTypes,
  validate: validatePhoneNumberWithError,
  patterns: phoneValidationPatterns,
};